import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {collection, getDocs, limit, orderBy, query} from 'firebase/firestore';
import {FaRobot, FaChevronDown, FaChevronUp, FaLock} from 'react-icons/fa';
import {db} from '../utils/firebase';
import {useAuth} from '../context/AuthContext';
import {useLang} from '../context/LanguageContext';

const AITicketsSection = () => {
    const navigate = useNavigate();
    const {t} = useLang();
    const {currentUser, hasPaid, hasVip} = useAuth();
    const hasAccess = !!currentUser && (hasPaid || hasVip);

    const [tickets, setTickets] = useState(null);
    const [error, setError] = useState(false);
    const [openId, setOpenId] = useState(null);

    useEffect(() => {
        if (!hasAccess) return;
        const load = async () => {
            try {
                const snap = await getDocs(query(collection(db, 'ainewtickets'), orderBy('date', 'desc'), limit(9)));
                const list = snap.docs.map(d => ({id: d.id, ...d.data()}));
                const latestDate = list.length ? list[0].date : null;
                setTickets(
                    list
                        .filter(tk => tk.date === latestDate)
                        .sort((a, b) => (a.target_odds || 0) - (b.target_odds || 0))
                );
            } catch (err) {
                console.error(err);
                setError(true);
                setTickets([]);
            }
        };
        load();
    }, [hasAccess]);

    const header = (
        <div className="flex items-center justify-center gap-2 mb-2">
            <FaRobot className="text-[#2ecc71]"/>
            <h2 className="text-white text-lg font-bold text-center">{t('txt.aiTicketsTitle')}</h2>
        </div>
    );

    if (!hasAccess) {
        return (
            <div className="max-w-md mx-auto mb-6 rounded-lg border-2 border-[#2ecc71] bg-[#2c3e50] p-4 text-center">
                {header}
                <div className="flex justify-center mb-2 text-yellow-300"><FaLock/></div>
                <p className="text-yellow-300 text-sm mb-3">{t('txt.aiTicketsLocked')}</p>
                <button
                    onClick={() => navigate(currentUser ? '/subscriptions' : '/login')}
                    className="px-4 py-2 bg-[#f39c12] hover:bg-orange-600 text-white text-sm font-bold rounded"
                >
                    {currentUser ? t('txt.aiTicketsLoginOrSubscribe') : t('txt.aiTicketsLoginButton')}
                </button>
                <p className="text-gray-300 text-xs italic mt-3">{t('txt.aiTicketsDisclaimer')}</p>
            </div>
        );
    }

    return (
        <div className="max-w-md mx-auto mb-6 rounded-lg border-2 border-[#2ecc71] bg-[#2c3e50] p-4">
            {header}
            <p className="text-gray-300 text-xs italic text-center mb-3">{t('txt.aiTicketsDisclaimer')}</p>

            {tickets === null && <p className="text-center text-white text-sm">...</p>}
            {error && <p className="text-center text-red-300 text-sm">{t('txt.aiTicketsError')}</p>}
            {tickets && tickets.length === 0 && !error && (
                <p className="text-center text-yellow-300 text-sm">{t('txt.aiTicketsEmpty')}</p>
            )}

            <div className="space-y-3">
                {(tickets || []).map(tk => {
                    const isOpen = openId === tk.id;
                    return (
                        <div key={tk.id} className="rounded-lg bg-[#34495e] border border-white/20 overflow-hidden">
                            <button
                                onClick={() => setOpenId(isOpen ? null : tk.id)}
                                className="w-full flex items-center justify-between gap-3 p-3 text-left"
                            >
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-white font-extrabold text-base">
                                            {t('txt.aiTicketsOdd')} {tk.target_odds}
                                        </span>
                                        <span className="bg-[#2ecc71] text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                                            <FaRobot size={10}/> {t('txt.aiTicketsBadge')}
                                        </span>
                                    </div>
                                    <p className="text-gray-300 text-xs mt-1">
                                        {tk.title} • {(tk.legs || []).length} {t('txt.aiTicketsMatches')}
                                    </p>
                                </div>
                                <span className="text-white shrink-0">
                                    {isOpen ? <FaChevronUp/> : <FaChevronDown/>}
                                </span>
                            </button>

                            {isOpen && (
                                <div className="px-3 pb-3 space-y-2">
                                    {(tk.legs || []).map((leg, idx) => (
                                        <div key={leg.match_id || idx} className="bg-white rounded p-3">
                                            <p className="font-bold text-sm">{leg.home_team} vs {leg.away_team}</p>
                                            <p className="text-xs text-gray-500">
                                                {leg.country}{leg.league ? ` - ${leg.league}` : ''} • {leg.date} {leg.time}
                                            </p>
                                            <p className="mt-1 text-sm">
                                                {leg.label} -{' '}
                                                <span className="font-semibold text-green-600">{leg.odds}</span>
                                            </p>
                                        </div>
                                    ))}
                                    <div className="text-white text-sm font-semibold pt-1">
                                        {t('txt.aiTicketsTotalOdds')}: {tk.total_odds}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default AITicketsSection;
